import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import OrderSuccess from "../../../modal/OrderSuccess";
import LogoutSuccessModal from "../../../modal/LogoutSuccessModal";
import { clearStoredAuthUser, getStoredAuthUser } from "../../../utils/authSession";
import {
    enrichStationWithThemeBackgrounds,
    readActiveStoredStation,
    resolveStationRouteSlug,
    type StoredStationRecord,
} from "../../../utils/stationFlowStorage";
import { AppDispatch } from "../../../redux/store";
import { clearFavourites } from "../../../redux/slices/favouritesSlice";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import type { AiSelfieArt } from "../../../services/aiSelfieArtService";
import { readPendingOrderNumbers } from "../../../utils/pendingPaymentOrder";

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const { slug } = useParams();
    const { stationData, selectedArt } = (location.state || {}) as {
        stationData?: StoredStationRecord;
        selectedArt?: Partial<AiSelfieArt> | null;
    };

    const themeStation = useMemo(
        () => enrichStationWithThemeBackgrounds(stationData || readActiveStoredStation(), selectedArt),
        [selectedArt, stationData]
    );

    const orderNumbers = useMemo(() => {
        const state = (location.state || {}) as {
            orderNumbers?: Array<string | number>;
            orderNumber?: string | number;
        };
        if (Array.isArray(state.orderNumbers) && state.orderNumbers.length > 0) {
            return state.orderNumbers.map((value) => String(value).trim()).filter(Boolean);
        }
        if (state.orderNumber != null && String(state.orderNumber).trim()) {
            return [String(state.orderNumber).trim()];
        }
        return readPendingOrderNumbers();
    }, [location.state]);

    const orderConfirmationRoute = useMemo(
        () => `/${resolveStationRouteSlug(themeStation, slug, selectedArt)}/order-confirmation`,
        [selectedArt, slug, themeStation]
    );

    const [showOrderModal, setShowOrderModal] = useState(true);
    const [showLogoutSuccessModal, setShowLogoutSuccessModal] = useState(false);

    const goToOrderConfirmation = useCallback(() => {
        navigate(orderConfirmationRoute, {
            replace: true,
            state: {
                stationData: themeStation,
                selectedArt,
                orderNumbers,
                orderNumber: orderNumbers[0],
            },
        });
    }, [navigate, orderConfirmationRoute, orderNumbers, selectedArt, themeStation]);

    const handleOrderModalHide = useCallback(() => {
        setShowOrderModal(false);

        // Logged in: confirm logout first, then show the created order(s).
        if (getStoredAuthUser()) {
            clearStoredAuthUser();
            dispatch(clearFavourites());
            setShowLogoutSuccessModal(true);
            return;
        }

        goToOrderConfirmation();
    }, [dispatch, goToOrderConfirmation]);

    const handleLogoutSuccessModalHide = useCallback(() => {
        setShowLogoutSuccessModal(false);
        goToOrderConfirmation();
    }, [goToOrderConfirmation]);

    return (
        <>
            <StationThemeLayout
                className="successPaymentSection paymentSuccessThemePage"
                station={themeStation}
                showStationLogo
            >
                <div className="h-100" />
            </StationThemeLayout>

            {showOrderModal && (
                <OrderSuccess show={showOrderModal} onHide={handleOrderModalHide} />
            )}
            {showLogoutSuccessModal && (
                <LogoutSuccessModal
                    show={showLogoutSuccessModal}
                    onHide={handleLogoutSuccessModalHide}
                />
            )}
        </>
    );
};

export default PaymentSuccess;
